import type { ReactNode } from 'react';
import { PageHeader } from '../components/PageHeader.js';
import { AuthenticatorCard } from '../components/AuthenticatorCard.js';
import { PasswordCard } from '../components/PasswordCard.js';
import { TimezonePicker } from '../components/TimezonePicker.js';
import { useCurrentUser } from '../auth/session-context.js';
import { effectiveTimezone, formatInstant } from '../lib/time.js';
import { Card } from '../ui/Card.js';

/**
 * Everything a person can change about themselves: their display time zone
 * (section 20) and their two credentials (section 17).
 *
 * Every role has this page. Section 17 is about what someone may do to the fleet,
 * not about whether they may look after their own account.
 */

export function AccountPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);

  return (
    <div className="page">
      <PageHeader
        title="Your account"
        description="Your sign-in details and how this console shows times to you."
      />

      <Card title="Profile">
        <dl className="details">
          <dt>Email</dt>
          <dd>{session.user.email}</dd>
          <dt>Role</dt>
          <dd>
            <span className="badge badge-accent">{session.user.role}</span>
          </dd>
          <dt>Account created</dt>
          <dd>{formatInstant(session.user.createdAt, { timezone })}</dd>
          <dt>Last sign-in</dt>
          <dd>
            {session.user.lastLoginAt === null
              ? 'This is the first one'
              : formatInstant(session.user.lastLoginAt, { timezone })}
          </dd>
        </dl>
      </Card>

      <Card
        title="Display time zone"
        description="Everything is stored in UTC and converted for you here."
      >
        <TimezonePicker />
      </Card>

      <PasswordCard />
      <AuthenticatorCard />
    </div>
  );
}
